import axios from '@/src/api/axios_init'
import { CACHE_TTL } from '@shared/utils'
import type { AxiosRequestConfig } from 'axios'
import type { UiPreferenceKey, UiPreferenceResponse } from './types/backend/ui-preference.api'
import lscache from 'lscache'

import type { IPostBaselineRequest } from '@features/analytics/types/baselineData.interface'
import qs from 'qs'
import {
  CONSUMPTION,
  CONSUMPTION_TRENDS,
  LOAD_PROFILE_DETAILS,
  LOCATIONS,
  LOCATIONS_V2,
  MEASURE_VALERI_CALCULATION_EXPORT,
  PEAKLOAD_TRENDS,
  SSO_STRATEGIES,
  VALERI_CALCULATION_EXPORT,
} from './apiRoutes'

import type { SupportedLanguage } from '@/src/shared/application/translation.service'
import type {
  IFetchBaselineRequest,
  IGetBaselineResponse,
  IUpdateOneBaselineResponse,
} from './types/backend/baseline.api'
import type {
  ICreateOneComparisonRequest,
  IDeleteOneComparisonRequest,
  IDeleteOneComparisonResponse,
  IFetchComparisonsRequest,
  IFetchComparisonsResponse,
  IFetchOneComparisonRequest,
  IFetchOneComparisonResponse,
  IUpdateOneComparisonRequest,
  TCreateOneComparisonResponse,
  TUpdateOneComparisonResponse,
} from './types/backend/comparison.api'
import type { PostConsumptionTrendsResponse } from './types/backend/consumption-trends.api'
import type {
  ICreateOneCustomDataMetaRequest,
  ICreateOneCustomDataMetaResponse,
  IFetchCustomDataMetaRequest,
  IFetchOneCustomTimeSeriesMetaDataResponse,
  TFetchCustomDataMetaResponse,
  TFetchOneCustomTimeSeriesDataResponse,
  TUpdateOneCustomDataMetaRequest,
  TUpdateOneCustomDataMetaResponse,
} from './types/backend/custom-data.api'
import type { FetchEnpiSeuOptionsResponse } from './types/backend/enpi.api'
import type { GetLoadProfileDetailsResponse } from './types/backend/load-profile-details.api'
import type { ILocationsStatsRequest, TLocationsStatsResponse } from './types/backend/location-stats.api'
import type {
  IFetchOneMeterInfraSettingsResponse,
  IUpdateOneMeterInfraSettingsRequest,
} from './types/backend/meter-Infrastructure-settings.api'
import type { PostPeakLoadTrendsResponse } from './types/backend/peakload-trends.api'
import type { ShortTermForecastRequest, ShortTermForecastResponse } from './types/backend/short-term-forecast.api'
import type { UserManagementPageQuery, UserManagementPageResponse } from './types/backend/user-management.api'
import {
    type ApplyTemplateDto,
    type AuthDto,
    type AvailableMainMeterDto,
    type BudgetControllerGetInvoicesData,
    type BudgetControllerGetInvoicesWithProcurementCostsData,
    type BudgetTrackingControllerGetCostsData,
    type BuildingControllerDeleteBuildingData,
    type BuildingControllerUpsertBuildingData,
    type CalculationsControllerGetLocationsMetricsCsvData,
    type CalculationsControllerGetLocationsMetricsData,
    type CityControllerGetAllCitiesData,
    type CompanyControllerGetCompanyLogoData,
    type CompanyControllerGetCompanyRegionsData,
    type CompanyControllerGetCompanyWhiteLabelLogoData,
    type CompanyEnergyBaselineConfigControllerUpsertData,
    type ConsultancyControllerRequestMeterCreationData,
    type ConsumptionControllerGetAggregatedConsumptionWithGranularityData,
    type ConsumptionControllerGetConsumptionSeriesData,
    type CreateCustomReportDto,
    type CreateEnpiDto,
    type CreateIsoDocumentDto,
    type CreateIsoGoalDto,
    type CreateIsoMeasureDto,
    type CreateIsoMilestoneDto,
    type CreateIsoRiskDto,
    type CreateMeterDocumentDto,
    type CreateMeterViewDto,
    type CreateReportModuleDto,
    type CreateSlpMeterReadingDto,
    type CreateSlpMeterRequestDto,
    type CreateUserDto,
    type CreateValeriDto,
    type CreateValeriPositionDto,
    type CreateViewNodeDto,
    type CreateVirtualDeviceDto,
    type DataCompletenessControllerGetDataCompletenessData,
    type DeviceControllerDeleteDeviceData,
    type DeviceControllerUpsertDeviceData,
    type EmberControllerAskChatAssistantData,
    type EmberControllerAskChecklistAssistantData,
    type EmberControllerAskDocumentAssistantData,
    type EmberControllerCancelMessageProcessingData,
    type EmberControllerGenerateReportModuleConfigData,
    type EmberControllerGetProposedQuestionsData,
    type GapsControllerGetGapsData,
    type GenerationLoadProfileDto,
    type InsightInboxControllerAssignData,
    type InsightInboxControllerBulkAssignData,
    type InsightInboxControllerBulkDismissData,
    type InsightInboxControllerBulkDismissGroupedData,
    type InsightInboxControllerBulkResolveData,
    type InsightInboxControllerBulkSetVisibilityData,
    type InsightInboxControllerConvertData,
    type InsightInboxControllerDismissData,
    type InsightInboxControllerFindAllData,
    type InsightInboxControllerFindOneData,
    type InsightInboxControllerInvestigateData,
    type InsightInboxControllerMarkViewedData,
    type InsightInboxControllerResolveData,
    type InsightInboxControllerSetVisibilityData,
    type InvoicesControllerUpdateNotesData,
    type InvoicesControllerUpdateStatusData,
    type IsoEnpiControllerCreateResponse,
    type IsoMeasureStatusEnum,
    type LoadControllerGetAggregatedMonthlyBaseloadsData,
    type LoadControllerGetAggregatedMonthlyPeakloadsData,
    type LoadSimulationControllerCreateSimulationData,
    type LoadSimulationControllerCreateSimulationResponse,
    type LoadSimulationControllerFindAllForCompanyIdData,
    type LoadSimulationControllerFindAllForCompanyIdResponse,
    type LoadSimulationControllerFindSimulationByIdResponse,
    type LoadSimulationControllerGetAvailableMainMetersData,
    type LoadSimulationControllerUpdateSimulationByIdData,
    type LoadSimulationControllerUpdateSimulationByIdResponse,
    type LocationControllerDeleteLocationData,
    type LocationControllerGetEnabledModulesForUserData,
    type LocationControllerGetLocationData,
    type LocationControllerGetLocationsWithBuildingsAndDevicesByCompanyData,
    type LocationControllerUpsertLocationData,
    type LocationProductionScheduleControllerBulkUpsertData,
    type LocationProductionScheduleControllerUpsertForLocationData,
    type MeterControllerGetBulkInfrastructureSettingsData,
    type MeterControllerGetEnergyEvaluationData,
    type MeterControllerGetMetersData,
    type MeterControllerGetMetersInfoByAllowedLocationsData,
    type MeterControllerPutBulkInfrastructureSettingsData,
    type MultivariateLinearRegressionControllerCreateRegressionData,
    type MultivariateLinearRegressionControllerDeleteRegressionData,
    type MultivariateLinearRegressionControllerGetActualVsModelSeriesData,
    type MultivariateLinearRegressionControllerGetAllRegressionsData,
    type MultivariateLinearRegressionControllerGetRegressionByIdData,
    type MultivariateLinearRegressionControllerUpdateRegressionData,
    type ObjectStorageV2PostResponseDto,
    type OcrExtractedCostDetailsDto,
    type PatchInsightInboxSettingsDto,
    type PatchMeasureStatusDto,
    type PatchMilestoneStatusDto,
    type RegionControllerDeleteCompanyRegionData,
    type RegionControllerUpsertCompanyRegionData,
    type ResponseReportModuleWithDataDto,
    type SlpConsumptionDto,
    type SpotMarketControllerGetCompanyPricesData,
    type SpotMarketControllerGetPricesData,
    type SsoLoginRequestDto,
    type SubmeterAttributionControllerGetAttributionData,
    type UpdateCostVerificationOverrideDto,
    type UpdateCustomReportDto,
    type UpdateEnpiDto,
    type UpdateIsoDocumentMetaDataDto,
    type UpdateIsoMeasureDto,
    type UpdateIsoMilestoneDto,
    type UpdateMeterViewDto,
    type UpdateReportModuleDto,
    type UpdateUserDto,
    type UpdateValeriDto,
    type UpdateValeriPositionDto,
    type UploadControllerUploadSlpManualConsumptionData,
    type UpsertCompanyValeriDefaultsDto,
    type UpsertGridUsagePriceDto,
    type UpsertPeakLoadPriceDto,
    type UserControllerGetUserByIdData,
    type UserControllerSelectCompanyData,
    authControllerSendPasswordResetEmail,
    budgetControllerGetInvoices,
    budgetControllerGetInvoicesWithProcurementCosts,
    budgetTrackingControllerGetCosts,
    buildingControllerDeleteBuilding,
    buildingControllerUpsertBuilding,
    calculationsControllerGetLocationsMetrics,
    calculationsControllerGetLocationsMetricsCsv,
    cityControllerGetAllCities,
    companyControllerGetCompany,
    companyControllerGetCompanyLogo,
    companyControllerGetCompanyRegions,
    companyControllerGetCompanyWhiteLabelLogo,
    companyControllerGetPremiumFeatures,
    companyEnergyBaselineConfigControllerGet,
    companyEnergyBaselineConfigControllerUpsert,
    consultancyControllerRequestMeterCreation,
    consumptionControllerGetAggregatedConsumptionWithGranularity,
    consumptionControllerGetConsumptionSeries,
    customReportControllerApplyTemplate,
    customReportControllerCreateCustomReport,
    customReportControllerCreateReportModule,
    customReportControllerDeleteCustomReport,
    customReportControllerDeleteReportModule,
    customReportControllerGenerateReportManually,
    customReportControllerGetAllCustomReports,
    customReportControllerGetCustomReport,
    customReportControllerGetCustomReportLogo,
    customReportControllerGetModule,
    customReportControllerGetModuleImage,
    customReportControllerGetReportModules,
    customReportControllerGetSentReport,
    customReportControllerGetSentReportLogo,
    customReportControllerGetSentReportModuleImage,
    customReportControllerGetSentReportModules,
    customReportControllerGetSentReportModulesWithData,
    customReportControllerGetSentReports,
    customReportControllerReorderReportModules,
    customReportControllerRequestReportingUpgrade,
    customReportControllerUpdateCustomReport,
    customReportControllerUpdateReportModule,
    dataCompletenessControllerGetDataCompleteness,
    deviceControllerDeleteDevice,
    deviceControllerUpsertDevice,
    documentTemplateControllerFindAll,
    documentTemplateControllerFindOne,
    emberControllerAskChatAssistant,
    emberControllerAskChecklistAssistant,
    emberControllerAskDocumentAssistant,
    emberControllerCancelMessageProcessing,
    emberControllerGenerateReportModuleConfig,
    emberControllerGetProposedQuestions,
    emissionsControllerGetEmissionsComparison,
    gapsControllerGetGaps,
    gridFeesControllerDeleteGridUsageFee,
    gridFeesControllerDeletePeakLoadPrice,
    gridFeesControllerGetGridUsageFees,
    gridFeesControllerGetPeakLoadPrices,
    gridFeesControllerUpsertGridUsageFee,
    gridFeesControllerUpsertPeakLoadPrice,
    insightInboxControllerAssign,
    insightInboxControllerBulkAssign,
    insightInboxControllerBulkDismiss,
    insightInboxControllerBulkDismissGrouped,
    insightInboxControllerBulkResolve,
    insightInboxControllerBulkSetVisibility,
    insightInboxControllerConvert,
    insightInboxControllerDismiss,
    insightInboxControllerFindAll,
    insightInboxControllerFindOne,
    insightInboxControllerGetDismissalReasonConfig,
    insightInboxControllerInvestigate,
    insightInboxControllerMarkViewed,
    insightInboxControllerResolve,
    insightInboxControllerSetVisibility,
    insightSettingsControllerApplyRecommendations,
    insightSettingsControllerGetForLocations,
    insightSettingsControllerGetForMeter,
    insightSettingsControllerGetRecommendationForMeter,
    insightSettingsControllerGetRecommendationsForLocations,
    insightSettingsControllerPatchForMeter,
    insightsOnboardingControllerComplete,
    insightsOnboardingControllerGetState,
    insightsOnboardingControllerStart,
    invitationControllerAccept,
    invitationControllerCreate,
    invitationControllerFindAll,
    invitationControllerRevoke,
    invitationControllerUpdate,
    invitationControllerValidate,
    invoicesControllerGetInvoiceById,
    invoicesControllerGetInvoices,
    invoicesControllerGetProcurementInvoiceById,
    invoicesControllerGetProcurementInvoices,
    invoicesControllerUpdateNotes,
    invoicesControllerUpdateOcrCostDetails,
    invoicesControllerUpdateOcrCostVerification,
    invoicesControllerUpdateStatus,
    isoChaptersControllerFindAll,
    isoChecklistControllerCreateChecklistByCompany,
    isoChecklistControllerFindByCompanyId,
    isoChecklistControllerLinkDocument,
    isoChecklistControllerMarkTaskCompleted,
    isoChecklistControllerMarkTaskInProgress,
    isoChecklistControllerMarkTaskOpen,
    isoChecklistControllerRemoveLinkDocument,
    isoControllerGetIsoOverview,
    isoDocumentsControllerCreate,
    isoDocumentsControllerDelete,
    isoDocumentsControllerFindAll,
    isoDocumentsControllerFindOneById,
    isoDocumentsControllerUpdateContent,
    isoDocumentsControllerUpdateMetaData,
    isoEnpiControllerAddComment,
    isoEnpiControllerDelete,
    isoEnpiControllerFindAll,
    isoEnpiControllerRecalculate,
    isoEnpiControllerUpdate,
    isoGoalsControllerCreate,
    isoGoalsControllerDelete,
    isoGoalsControllerFindAll,
    isoGoalsControllerUpdate,
    isoMeasuresControllerActivateAutoTracking,
    isoMeasuresControllerAddComment,
    isoMeasuresControllerCreate,
    isoMeasuresControllerCreateMilestone,
    isoMeasuresControllerDelete,
    isoMeasuresControllerDeleteMeasureSavings,
    isoMeasuresControllerDeleteMilestone,
    isoMeasuresControllerFindAll,
    isoMeasuresControllerFindOneById,
    isoMeasuresControllerPatchMilestoneStatus,
    isoMeasuresControllerPatchStatus,
    isoMeasuresControllerUpdate,
    isoMeasuresControllerUpdateMilestone,
    isoMeasuresControllerUpdateMilestonesOrder,
    isoMeasuresControllerValidateStatus,
    isoRiskControllerCreate,
    isoRiskControllerDelete,
    isoRiskControllerFindAll,
    isoRiskControllerUpdate,
    loadControllerGetAggregatedMonthlyBaseloads,
    loadControllerGetAggregatedMonthlyPeakloads,
    loadSimulationControllerDeleteSimulationById,
    loadSimulationControllerFindSimulationById,
    loadSimulationControllerGetSimulationCsv,
    locationControllerDeleteLocation,
    locationControllerGetEnabledModulesForUser,
    locationControllerGetLocation,
    locationControllerGetLocationsWithBuildingsAndDevicesByCompany,
    locationControllerUpsertLocation,
    locationProductionScheduleControllerBulkUpsert,
    locationProductionScheduleControllerGetForCaller,
    locationProductionScheduleControllerUpsertForLocation,
    machineCategoryControllerGetAllCategories,
    meterControllerCreateMeterEvaluation,
    meterControllerCreateSlpMeter,
    meterControllerDeleteMeterEvaluation,
    meterControllerDeleteSlpMeter,
    meterControllerGetBulkInfrastructureSettings,
    meterControllerGetEnergyEvaluation,
    meterControllerGetMeters,
    meterControllerGetMetersInfoByAllowedLocations,
    meterControllerGetSlpMeter,
    meterControllerPutBulkInfrastructureSettings,
    meterControllerUpdateSlpMeter,
    meterDocumentControllerCreateMeterDocument,
    meterDocumentControllerDeleteMeterDocument,
    meterDocumentControllerDownload,
    meterSeriesControllerGetSeriesForMeter,
    meterViewControllerCreate,
    meterViewControllerCreateNode,
    meterViewControllerDelete,
    meterViewControllerDeleteNode,
    meterViewControllerFindAll,
    meterViewControllerFindById,
    meterViewControllerUpdate,
    meterViewControllerUpdateFolderName,
    meterViewControllerUpdateNodeParent,
    multivariateLinearRegressionControllerCreateRegression,
    multivariateLinearRegressionControllerDeleteRegression,
    multivariateLinearRegressionControllerGetActualVsModelSeries,
    multivariateLinearRegressionControllerGetAllRegressions,
    multivariateLinearRegressionControllerGetRegressionById,
    multivariateLinearRegressionControllerUpdateRegression,
    objectStorageControllerDelete,
    objectStorageV2ControllerDeleteByUid,
    regionControllerDeleteCompanyRegion,
    regionControllerUpsertCompanyRegion,
    sankeyControllerGetMeterViewSankey,
    savingsFlowControllerCreateMeasureFromInsight,
    slpConsumptionControllerGetReferencesForDeviceId,
    slpConsumptionControllerGetSlpConsumption,
    slpConsumptionControllerUpdateSlpConsumption,
    slpMeterReadingControllerGetReadings,
    slpMeterReadingControllerReplaceReadings,
    spotMarketControllerGetCompanyPrices,
    spotMarketControllerGetPrices,
    submeterAttributionControllerGetAttribution,
    uploadControllerUploadSlpManualConsumption,
    userControllerCreate,
    userControllerDeleteUser,
    userControllerDeleteUserProfilePicture,
    userControllerGetFrontendUsers,
    userControllerGetUserById,
    userControllerGetUserProfilePicture,
    userControllerGetUserProfilePictureById,
    userControllerSelectCompany,
    userControllerUpdate,
    userControllerUploadUserProfilePicture,
    valeriControllerCalculateValeri,
    valeriControllerCreateValeri,
    valeriControllerCreateValeriPosition,
    valeriControllerDeleteValeri,
    valeriControllerDeleteValeriPosition,
    valeriControllerFindOneById,
    valeriControllerGetCompanyDefaults,
    valeriControllerGetValeriByMeasureId,
    valeriControllerGetValeriCalculationResults,
    valeriControllerGetValeriPositions,
    valeriControllerUpdateValeri,
    valeriControllerUpdateValeriPosition,
    valeriControllerUpsertCompanyDefaults,
    virtualMeterControllerCreateVirtualDevice,
} from './types/generated-types'

import { SSO_AUTH_LOGIN, SSO_AUTH_SIGNUP } from "@api/data/authentication.service"
import {
  type CompanyFrontendConfigurationControllerGetCompanyConfigurationData,
  type CustomReferenceBaseLoadControllerDeleteCustomReferenceBaseLoadData,
  type CustomReferenceBaseLoadControllerUploadCustomReferenceBaseLoadData,
  type LoadAdjustmentsControllerCreateAbsoluteQuantityData,
  type LoadAdjustmentsControllerCreateCompanyHolidaysData,
  type LoadAdjustmentsControllerCreatePercentageQuantityData,
  type LoadAdjustmentsControllerCreateReferenceBaseloadCorrectionData,
  type LoadAdjustmentsControllerCreateRenewableProductionData,
  type LoadAdjustmentsControllerDeleteAdjustmentData,
  type LoadAdjustmentsControllerFindAdjustmentsByMeasureSimulationData,
  type LoadAdjustmentsControllerFindAllAdjustmentsData,
  type LoadAdjustmentsControllerFindAllMeasureSimulationCandidatesData,
  type LoadAdjustmentsControllerSyncMeasureSimulationData,
  type LoadAdjustmentsControllerUpdateAbsoluteQuantityData,
  type LoadAdjustmentsControllerUpdateCompanyHolidaysData,
  type LoadAdjustmentsControllerUpdatePercentageQuantityData,
  type LoadAdjustmentsControllerUpdateReferenceBaseloadCorrectionData,
  type LoadAdjustmentsControllerUpdateRenewableProductionData,
  type LoadPlanningControllerGetAllLoadPlanningsData,
  type LoadPlanningControllerGetEarliestConsumptionPerLocationData,
  type LoadPlanningControllerGetForecastData,
  type LoadPlanningControllerGetPreviewForecastData,
  type LoadPlanningControllerRequestLoadProfileDataData,
  type LoadPlanningControllerUpdateLoadPlanningData,
  type MarketTrackingControllerRetrieveMarketTrackingData,
  type MarketTrackingControllerRetrieveMarketTrackingForAllProductsData,
  type PlannedStrategyControllerCreatePlannedStrategyData,
  type PlannedStrategyControllerDeletePlannedStrategyData,
  type PlannedStrategyControllerRetrieveAllPlannedStrategiesData,
  type PlannedStrategyControllerRetrievePlannedStrategyData,
  type PortfolioOverviewControllerGetPortfolioOverviewData,
  type PortfolioOverviewControllerGetPortfolioTimeseriesData,
  type PortfolioOverviewControllerRequestPortfolioStrategyData,
  type PortfolioOverviewControllerRequestUpdatePortfolioStrategyData,
  type PricesControllerGetAllProductPricesData,
  type PricesControllerRetrievePricesData,
  type SavingsControllerGetData,
  type StaticStrategyControllerCreateStrategyData,
  type StaticStrategyControllerDeleteStaticStrategyDocumentsData,
  type StaticStrategyControllerDeleteStrategyData,
  type StaticStrategyControllerGetStaticStrategyData,
  type StaticStrategyControllerGetStaticStrategyDocumentsData,
  type StaticStrategyControllerRetrieveAllStaticStrategiesData,
  type StaticStrategyControllerUpdateStrategyData,
  type StaticStrategyControllerUploadStrategyDocumentsData,
  type StrategyControllerDeleteStrategyDocumentsData,
  type StrategyControllerFetchStrategyOverviewsData,
  type StrategyControllerGetStrategyData,
  type StrategyControllerGetStrategyDocumentsData,
  type StrategyControllerRetrieveAllStrategiesData,
  type StrategyControllerUploadStrategyDocumentsData,
  type StrategySpecificReferencePriceControllerCreateData,
  type StrategySpecificReferencePriceControllerCreateOrUpdateStrategySpecificReferencePriceData,
  companyFrontendConfigurationsControllerGetCompanyConfiguration,
  customReferenceBaseLoadControllerDeleteCustomReferenceBaseLoad,
  customReferenceBaseLoadControllerUploadCustomReferenceBaseLoad,
  loadAdjustmentsControllerCreateAbsoluteQuantity,
  loadAdjustmentsControllerCreateCompanyHolidays,
  loadAdjustmentsControllerCreatePercentageQuantity,
  loadAdjustmentsControllerCreateReferenceBaseloadCorrection,
  loadAdjustmentsControllerCreateRenewableProduction,
  loadAdjustmentsControllerDeleteAdjustment,
  loadAdjustmentsControllerFindAdjustmentsByMeasureSimulation,
  loadAdjustmentsControllerFindAllAdjustments,
  loadAdjustmentsControllerFindAllMeasureSimulationCandidates,
  loadAdjustmentsControllerSyncMeasureSimulation,
  loadAdjustmentsControllerUpdateAbsoluteQuantity,
  loadAdjustmentsControllerUpdateCompanyHolidays,
  loadAdjustmentsControllerUpdatePercentageQuantity,
  loadAdjustmentsControllerUpdateReferenceBaseloadCorrection,
  loadAdjustmentsControllerUpdateRenewableProduction,
  loadPlanningControllerGetAllLoadPlannings,
  loadPlanningControllerGetEarliestConsumptionPerLocation,
  loadPlanningControllerGetForecast,
  loadPlanningControllerGetPreviewForecast,
  loadPlanningControllerRequestLoadProfileData,
  loadPlanningControllerUpdateLoadPlanning,
  marketTrackingControllerRetrieveMarketTracking,
  marketTrackingControllerRetrieveMarketTrackingForAllProducts,
  plannedStrategyControllerCreatePlannedStrategy,
  plannedStrategyControllerDeletePlannedStrategy,
  plannedStrategyControllerRetrieveAllPlannedStrategies,
  plannedStrategyControllerRetrievePlannedStrategy,
  portfolioOverviewControllerGetPortfolioOverview,
  portfolioOverviewControllerGetPortfolioTimeseries,
  portfolioOverviewControllerRequestPortfolioStrategy,
  portfolioOverviewControllerRequestUpdatePortfolioStrategy,
  pricesControllerGetAllProductPrices,
  pricesControllerRetrievePrices,
  savingsControllerGet,
  staticStrategyControllerCreateStrategy,
  staticStrategyControllerDeleteStaticStrategyDocuments,
  staticStrategyControllerDeleteStrategy,
  staticStrategyControllerGetStaticStrategy,
  staticStrategyControllerGetStaticStrategyDocuments,
  staticStrategyControllerRetrieveAllStaticStrategies,
  staticStrategyControllerUpdateStrategy,
  staticStrategyControllerUploadStrategyDocuments,
  strategyControllerDeleteStrategyDocuments,
  strategyControllerFetchStrategyOverviews,
  strategyControllerGetStrategy,
  strategyControllerGetStrategyDocuments,
  strategyControllerRetrieveAllStrategies,
  strategyControllerUploadStrategyDocuments,
  strategySpecificReferencePriceControllerCreate,
  strategySpecificReferencePriceControllerCreateOrUpdateStrategySpecificReferencePrice,
} from "@api/types/procurement"
import {
  getConsumptionTrendsPayload,
  getLoadProfileDetailsPayload,
  postConsumptionTrendsPayload,
  postPeakLoadTrendsPayload,
} from './apiPayloadHelpers'
import type { LocationsInfoResponse } from './types/backend/locations-info.api'
import type { IFetchTimeSeriesData } from './types/backend/timeSeries.api'
import type {
  ICreateOneVisualizationRequest,
  ICreateOneVisualizationResponse,
  IDeleteOneVisualizationRequest,
  IDeleteOneVisualizationResponse,
  IFetchOneVisualizationRequest,
  IFetchOneVisualizationResponse,
  IFetchVisualizationsRequest,
  IFetchVisualizationsResponse,
  IUpdateOneVisualizationRequest,
  IUpdateOneVisualizationResponse,
} from './types/backend/visualization.api'

export interface SsoProvider {
  id: string;
  name: string;
  company: string;
  payload: Record<string, any>;
}

// TODO: integrate better within API object.
export const getCompany = async companyId => {
  const companyRes = await axios.get('/firebase/company', { params: { companyId } })
  return companyRes.data
}

export const API = {
  meters: {
    fetch: (data: MeterControllerGetMetersData) => meterControllerGetMeters(data),
    energyEvaluation: (data: MeterControllerGetEnergyEvaluationData) => meterControllerGetEnergyEvaluation(data),
    createEvaluation: (meterId: number, contextParams: { locationId?: number; companyId?: number; buildingId?: number; regionId?: number | string }) => meterControllerCreateMeterEvaluation({
      requestBody: {
        meterId,
        ...contextParams
      } as any
    }),
    deleteEvaluation: (meterId: number) => meterControllerDeleteMeterEvaluation({ meterId }),
    documents: {
      addDocument: (dto: CreateMeterDocumentDto) => meterDocumentControllerCreateMeterDocument({ requestBody: dto }),
      deleteDocument: (documentId: number) => meterDocumentControllerDeleteMeterDocument({ id: documentId }),
      downloadDocument: (documentId: number) => meterDocumentControllerDownload({ id: documentId })
    }
  },

  virtualMeters: {
    create: (data: CreateVirtualDeviceDto) => virtualMeterControllerCreateVirtualDevice({ requestBody: data })
  },

  budget: {
    fetch: (params: BudgetControllerGetInvoicesData) => budgetControllerGetInvoices(params),
    fetchV4: (params: BudgetControllerGetInvoicesWithProcurementCostsData) => budgetControllerGetInvoicesWithProcurementCosts(params),
  },

  budgetTracking: {
    fetch: (params: BudgetTrackingControllerGetCostsData) => budgetTrackingControllerGetCosts(params),
  },

  company: {
    getLogo: (data: CompanyControllerGetCompanyLogoData) => companyControllerGetCompanyLogo(data),
    getWhiteLabelLogo: (data: CompanyControllerGetCompanyWhiteLabelLogoData) => companyControllerGetCompanyWhiteLabelLogo(data),
    getCompanyRegions: (data: CompanyControllerGetCompanyRegionsData) => companyControllerGetCompanyRegions(data),
    getCompany: (companyId: number) => companyControllerGetCompany({ companyId }),
    getPremiumFeatures: (companyId: number) => companyControllerGetPremiumFeatures({ companyId }),
  },

  location: {
    getEnabledModules: async (data: LocationControllerGetEnabledModulesForUserData) => locationControllerGetEnabledModulesForUser(data),
    getLocation: async (data: LocationControllerGetLocationData) => locationControllerGetLocation(data),
    getLocationAndBuildings: async (data: LocationControllerGetLocationsWithBuildingsAndDevicesByCompanyData) => locationControllerGetLocationsWithBuildingsAndDevicesByCompany(data),
    upsertLocation: async (data: LocationControllerUpsertLocationData) => locationControllerUpsertLocation(data),
    deleteLocation: async (data: LocationControllerDeleteLocationData) => locationControllerDeleteLocation(data),
  },

  building: {
    upsertBuilding: async (data: BuildingControllerUpsertBuildingData) => buildingControllerUpsertBuilding(data),
    deleteBuilding: async (data: BuildingControllerDeleteBuildingData) => buildingControllerDeleteBuilding(data),
  },

  devices: {
    upsertDevice: async (data: DeviceControllerUpsertDeviceData) => deviceControllerUpsertDevice(data),
    deleteDevice: async (data: DeviceControllerDeleteDeviceData) => deviceControllerDeleteDevice(data),
  },

  consultancy: {
    requestMeterCreation: async (data: ConsultancyControllerRequestMeterCreationData) => consultancyControllerRequestMeterCreation(data),
  },

  city: {
    fetchAll: async (data: CityControllerGetAllCitiesData) => cityControllerGetAllCities(data),
  },

  load: {
    // Same function as methods above but for normalized data (USE_NORMALIZED_TABLE)
    getAggregatedMonthlyBaseloads: (params: LoadControllerGetAggregatedMonthlyBaseloadsData) => loadControllerGetAggregatedMonthlyBaseloads(params),
    getAggregatedMonthlyPeakloads: (params: LoadControllerGetAggregatedMonthlyPeakloadsData) => loadControllerGetAggregatedMonthlyPeakloads(params),
  },

  locationsinfo: {
    fetch: (companyId: string, userId: string, { signal }: { signal?: AbortSignal }) => axios.get<LocationsInfoResponse>('/firebase/locations-info', { params: { companyId, userId }, signal }),
  },

  users: {
    fetch: (queryParams: {
      companyId?: number,
      locationId?: number,
      regionId?: number,
      email?: string,
      firebaseId?: string,
      locationFirebaseId?: string
    }) => userControllerGetFrontendUsers(queryParams),
    createUser: (user: CreateUserDto) => userControllerCreate({ requestBody: user }),
    updateUser: (userId: string, user: UpdateUserDto) => userControllerUpdate({ userId, requestBody: user }),
    deleteUser: (userId: number, userFirebaseId: string) => userControllerDeleteUser({ requestBody: { userId, firebaseId: userFirebaseId } }),
    selectCompany: (data: UserControllerSelectCompanyData) => userControllerSelectCompany(data),
    getById: (data: UserControllerGetUserByIdData) => userControllerGetUserById(data),
    getProfilePicture: () => userControllerGetUserProfilePicture(),
    getProfilePictureById: (id: number) => userControllerGetUserProfilePictureById({ id }),
    uploadProfilePicture: (file: File) => userControllerUploadUserProfilePicture({ formData: { file } }),
    deleteProfilePicture: () => userControllerDeleteUserProfilePicture(),
  },

  invitations: {
    validate: (token: string) => invitationControllerValidate({ token }),
    accept: (data: { token: string; password: string }) => invitationControllerAccept({ requestBody: data }),
    fetchMany: (companyId: number) => invitationControllerFindAll({ companyId }),
    fetchUserManagementPage: async (params: UserManagementPageQuery) =>
      (await axios.get<UserManagementPageResponse>('/v2/invitations/user-management', { params })).data,
    create: (data: any) => invitationControllerCreate({ requestBody: data }),
    update: (id: number, data: any) => invitationControllerUpdate({ id, requestBody: data }),
    revoke: (id: number) => invitationControllerRevoke({ id }),
  },

  enpi: {
    createOne: (data: CreateEnpiDto) => axios.post<IsoEnpiControllerCreateResponse>('/iso/enpis', data),
    updateOne: (id: number, updateDto: UpdateEnpiDto) => isoEnpiControllerUpdate({ id, requestBody: updateDto }),
    deleteOne: (id: number) => isoEnpiControllerDelete({ id }),
    fetch: (companyId: number) => isoEnpiControllerFindAll({ companyId }),
    createComment: (id: number, comment: string, createUser: string) => isoEnpiControllerAddComment({ id, requestBody: { content: comment, createUser: createUser } }),
    recalculateOne: (id: number) => isoEnpiControllerRecalculate({ id }),
  },

  iso: {
    fetchOverview: (userEmail: string, companyId: number) => isoControllerGetIsoOverview({ userId: userEmail, companyId: companyId })
  },

  isoChecklist: {
    fetchLatest: (companyId: number) => isoChecklistControllerFindByCompanyId({ companyId }),
    createChecklist: (companyId: number) => isoChecklistControllerCreateChecklistByCompany({ companyId }),
    markTaskCompleted: (checklistId: number, taskId: number) => isoChecklistControllerMarkTaskCompleted({ taskId, id: checklistId }),
    linkDocument: (checklistId: number, taskId: number, documentId: number) => isoChecklistControllerLinkDocument({ taskId, id: checklistId, requestBody: { documentId } }),
    removeLinkedDocument: (checklistId: number, taskId: number, documentId: number) => isoChecklistControllerRemoveLinkDocument({ taskId, id: checklistId, documentId }),
    markTaskOpen: (checklistId: number, taskId: number) => isoChecklistControllerMarkTaskOpen({ taskId, id: checklistId }),
    markTaskInProgress: (checklistId: number, taskId: number) => isoChecklistControllerMarkTaskInProgress({ taskId, id: checklistId }),
  },

  isoMeasures: {
    createOne: (measure: CreateIsoMeasureDto) => isoMeasuresControllerCreate({ requestBody: measure }),
    updateOne: (id: number, measure: UpdateIsoMeasureDto) => isoMeasuresControllerUpdate({ id, requestBody: measure }),
    createComment: (id: number, comment: string, createUser: string, mentionedUsers?: string[]) =>
      isoMeasuresControllerAddComment({ id, requestBody: { content: comment, createUser, mentionedUsers } }),
    fetchOne: (measureId: number) => isoMeasuresControllerFindOneById({ id: measureId }),
    fetch: (companyId: number, locationId?: number, statuses?: IsoMeasureStatusEnum[]) => isoMeasuresControllerFindAll({ companyId, locationId, statuses }),
    deleteMeasure: (measureId: number) => isoMeasuresControllerDelete({ id: measureId }),
    patchStatus: (measureId: number, statusUpdate: PatchMeasureStatusDto) => isoMeasuresControllerPatchStatus({ id: measureId, requestBody: statusUpdate }),
    deleteMeasureSavings: (measureId: number) => isoMeasuresControllerDeleteMeasureSavings({ id: measureId }),
    activateAutoTracking: (measureId: number) => isoMeasuresControllerActivateAutoTracking({ id: measureId }),
    linkSuccessMeasurementAnalyticsBaseline: (measureId: number, analyticsBaselineId: number) =>
      axiosPost(`/iso/measures/${measureId}/success-measurement/analytics-baseline`, { analyticsBaselineId }),
    // validate status update
    validateMeasureStatusUpdate: (measureId: number, statusUpdate: PatchMeasureStatusDto) => isoMeasuresControllerValidateStatus({ id: measureId, requestBody: statusUpdate }),
    // milestones
    createMilestone: (measureId: number, milestoneDto: CreateIsoMilestoneDto) => isoMeasuresControllerCreateMilestone({ id: measureId, requestBody: milestoneDto }),
    updateMilestone: ({ measureId, milestoneId, requestBody }: { measureId: number, milestoneId: number, requestBody: UpdateIsoMilestoneDto }) => isoMeasuresControllerUpdateMilestone({ id: measureId, milestoneId, requestBody }),
    updateMilestoneStatus: ({ measureId, milestoneId, requestBody }: { measureId: number, milestoneId: number, requestBody: PatchMilestoneStatusDto }) => isoMeasuresControllerPatchMilestoneStatus({ id: measureId, milestoneId, requestBody }),
    updateMilestoneOrder: ({ milestoneId, order }: { milestoneId: number, order: number[] }) => isoMeasuresControllerUpdateMilestonesOrder({ id: milestoneId, requestBody: { order } }),
    deleteMilestone: ({ measureId, milestoneId }: { measureId: number, milestoneId: number }) => isoMeasuresControllerDeleteMilestone({ id: measureId, milestoneId }),
    // export
    exportValeriCalculationResult: async (measureId: number): Promise<Blob> =>
      axiosGet(MEASURE_VALERI_CALCULATION_EXPORT.replace("{id}", measureId.toString()), { responseType: 'blob' })
  },

  valeri: {
    createValeri: (valeri: CreateValeriDto) => valeriControllerCreateValeri({ requestBody: valeri }),
    getCalculatedResults: (companyId: number, locationId?: number, measureId?: number) => valeriControllerGetValeriCalculationResults({ companyId, locationId, measureId }),
    getValeriByMeasureId: (measureId: number) => valeriControllerGetValeriByMeasureId({ measureId }),
    getValeriById: (valeriId: number) => valeriControllerFindOneById({ id: valeriId }),
    fetchValeriPositions: (valeriId: number) => valeriControllerGetValeriPositions({ id: valeriId }),
    computeValeriResult: (valeriId: number) => valeriControllerCalculateValeri({ id: valeriId }),
    updateValeri: (valeriId: number, valeri: UpdateValeriDto) => valeriControllerUpdateValeri({ id: valeriId, requestBody: valeri }),
    exportValeri: (id: number): Promise<Blob> =>
      axiosGet(VALERI_CALCULATION_EXPORT.replace("{id}", id.toString()), { responseType: 'blob' }),
    deleteValeri: (valeriId: number) => valeriControllerDeleteValeri({ id: valeriId }),
    updateValeriPosition: (valeriId: number, positionId: number, valeri: UpdateValeriPositionDto) => valeriControllerUpdateValeriPosition({ id: valeriId, positionId: positionId, requestBody: valeri }),
    createValeriPosition: (valeriId: number, valeri: CreateValeriPositionDto) => valeriControllerCreateValeriPosition({ id: valeriId, requestBody: valeri }),
    deleteValeriPosition: (valeriId: number, positionId: number) => valeriControllerDeleteValeriPosition({ id: valeriId, positionId: positionId }),
    getValeriDefaults: (companyId: number) => valeriControllerGetCompanyDefaults({ companyId }),
    upsertValeriDefaults: (companyId: number, requestBody: UpsertCompanyValeriDefaultsDto) => valeriControllerUpsertCompanyDefaults({ companyId, requestBody }),
  },
  isoGoals: {
    fetch: (data: { companyId: number }) => isoGoalsControllerFindAll({ companyId: data.companyId }),
    createOne: (data: CreateIsoGoalDto) => isoGoalsControllerCreate({ requestBody: data }),
    updateOne: (id: number, dto: CreateIsoGoalDto) => isoGoalsControllerUpdate({ id: id, requestBody: dto }),
    deleteOne: (goalId: number) => isoGoalsControllerDelete({ id: goalId }),
  },

  objectStorage: {
    uploadFile: (formData: any) => axios.post('/object-storage', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    removeFile: (fileId: number) => objectStorageControllerDelete({ fileId }),
    downloadLoadProfileTemplate: (companyId: number): Promise<Blob> =>
      axiosGet<Blob>(`/object-storage/consultants/load-profile-template?companyId=${companyId}`, { responseType: 'blob' }),
  },

  objectStorageV2: {
    // The generated fetch client rebuilds FormData via Object.entries(), which drops a browser
    // FormData's fields (files) and yields a 400. Send the multipart body via raw axios instead.
    uploadFile: (formData: FormData): Promise<ObjectStorageV2PostResponseDto[]> =>
      axios
        .post<ObjectStorageV2PostResponseDto[]>('/v2/object-storage', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(response => response.data),
    deleteFileByUid: (uid: string) => objectStorageV2ControllerDeleteByUid({ uid }),
  },

  isoDocuments: {
    fetch: (data: { locationId?: number, companyId?: number, chapterId?: number, measureId?: number }) => isoDocumentsControllerFindAll({ locationId: data.locationId, companyId: data.companyId, chapterId: data.chapterId, measureId: data.measureId }),
    fetchOne: (documentId: number) => isoDocumentsControllerFindOneById({ id: documentId }),
    delete: (documentId: number) => isoDocumentsControllerDelete({ id: documentId }),
    updateMetaData: (id: number, document: UpdateIsoDocumentMetaDataDto) => isoDocumentsControllerUpdateMetaData({ id, requestBody: document }),
    updateEcoplanetDocument: (id: number, content: string) => isoDocumentsControllerUpdateContent({ id, requestBody: { content: content } }),
    createDocument: (document: CreateIsoDocumentDto) => isoDocumentsControllerCreate({ requestBody: document })
  },

  documentTemplates: {
    fetchStaticTemplate: () => documentTemplateControllerFindAll({ isStatic: true }),
    fetchOneTemplate: (templateId: number) => documentTemplateControllerFindOne({ id: templateId }),
  },

  chapters: {
    fetch: () => isoChaptersControllerFindAll(),
  },

  isoRisk: {
    fetch: (companyId: number) => isoRiskControllerFindAll({ companyId }),
    createOne: (createDto: CreateIsoRiskDto) => isoRiskControllerCreate({ requestBody: createDto }),
    updateOne: (id: number, createDto: CreateIsoRiskDto) => isoRiskControllerUpdate({ id, requestBody: createDto }),
    deleteOne: (id: number) => isoRiskControllerDelete({ id })
  },

  companyBaselineConfig: {
    fetch: (companyId: number) => companyEnergyBaselineConfigControllerGet({ companyId }),
    upsert: (params: CompanyEnergyBaselineConfigControllerUpsertData) => companyEnergyBaselineConfigControllerUpsert(params),
  },

  locationProductionSchedules: {
    fetchAll: () => locationProductionScheduleControllerGetForCaller(),
    update: (data: LocationProductionScheduleControllerUpsertForLocationData) =>
      locationProductionScheduleControllerUpsertForLocation(data),
    bulkUpsert: (data: LocationProductionScheduleControllerBulkUpsertData) => locationProductionScheduleControllerBulkUpsert(data),
  },

  procurements: {
    fetchStrategyOverviews: (data: StrategyControllerFetchStrategyOverviewsData) => strategyControllerFetchStrategyOverviews(data),

    // @ts-ignore
    createOne: (formData: FormData) => axios.post('/procurement/data/documents', formData),

    fetchSavings: (data: SavingsControllerGetData) => savingsControllerGet(data),
    fetchMarketTracks: (data: MarketTrackingControllerRetrieveMarketTrackingData) => marketTrackingControllerRetrieveMarketTracking(data),
    fetchMarketTracksForAllProducts: (data: MarketTrackingControllerRetrieveMarketTrackingForAllProductsData) => marketTrackingControllerRetrieveMarketTrackingForAllProducts(data),
    fetchPricesForProduct: (data: PricesControllerRetrievePricesData) => pricesControllerRetrievePrices(data),
    fetchPricesForProducts: (data: PricesControllerGetAllProductPricesData) => pricesControllerGetAllProductPrices(data),
    fetchCompanyConfig: (data: CompanyFrontendConfigurationControllerGetCompanyConfigurationData) => companyFrontendConfigurationsControllerGetCompanyConfiguration(data),

    fetchHedgedStrategies: (data: StrategyControllerRetrieveAllStrategiesData) => strategyControllerRetrieveAllStrategies(data),
    fetchHedgedStrategy: (data: StrategyControllerGetStrategyData) => strategyControllerGetStrategy(data),

    fetchStaticStrategies: (data: StaticStrategyControllerRetrieveAllStaticStrategiesData) => staticStrategyControllerRetrieveAllStaticStrategies(data),
    fetchStaticStrategy: (data: StaticStrategyControllerGetStaticStrategyData) => staticStrategyControllerGetStaticStrategy(data),
    createStaticStrategy: (data: StaticStrategyControllerCreateStrategyData) => staticStrategyControllerCreateStrategy(data),
    updateStaticStrategy: (data: StaticStrategyControllerUpdateStrategyData) => staticStrategyControllerUpdateStrategy(data),
    deleteStaticStrategy: (data: StaticStrategyControllerDeleteStrategyData) => staticStrategyControllerDeleteStrategy(data),

    fetchPlannedStrategies: (data: PlannedStrategyControllerRetrieveAllPlannedStrategiesData) => plannedStrategyControllerRetrieveAllPlannedStrategies(data),
    fetchPlannedStrategy: (data: PlannedStrategyControllerRetrievePlannedStrategyData) => plannedStrategyControllerRetrievePlannedStrategy(data),
    createPlannedStrategy: (data: PlannedStrategyControllerCreatePlannedStrategyData) => plannedStrategyControllerCreatePlannedStrategy(data),
    deletePlannedStrategy: (data: PlannedStrategyControllerDeletePlannedStrategyData) => plannedStrategyControllerDeletePlannedStrategy(data),

    fetchMeasureSimulationCandidates: (data: LoadAdjustmentsControllerFindAllMeasureSimulationCandidatesData) => loadAdjustmentsControllerFindAllMeasureSimulationCandidates(data),
    syncMeasureSimulationAsAdjustment: (data: LoadAdjustmentsControllerSyncMeasureSimulationData) => loadAdjustmentsControllerSyncMeasureSimulation(data),
    fetchAdjustmentsByMeasureSimulation: (data: LoadAdjustmentsControllerFindAdjustmentsByMeasureSimulationData) => loadAdjustmentsControllerFindAdjustmentsByMeasureSimulation(data),

    fetchAllAdjustments: (data: LoadAdjustmentsControllerFindAllAdjustmentsData) => loadAdjustmentsControllerFindAllAdjustments(data),
    createAbsoluteQuantityAdjustment: (data: LoadAdjustmentsControllerCreateAbsoluteQuantityData) => loadAdjustmentsControllerCreateAbsoluteQuantity(data),
    createPercentageQuantityAdjustment: (data: LoadAdjustmentsControllerCreatePercentageQuantityData) => loadAdjustmentsControllerCreatePercentageQuantity(data),
    createCompanyHolidaysAdjustment: (data: LoadAdjustmentsControllerCreateCompanyHolidaysData) => loadAdjustmentsControllerCreateCompanyHolidays(data),
    createReferenceBaseloadCorrection: (data: LoadAdjustmentsControllerCreateReferenceBaseloadCorrectionData) => loadAdjustmentsControllerCreateReferenceBaseloadCorrection(data),
    createRenewableProductionAdjustment: (data: LoadAdjustmentsControllerCreateRenewableProductionData) => loadAdjustmentsControllerCreateRenewableProduction(data),

    updateAbsoluteQuantityAdjustment: (data: LoadAdjustmentsControllerUpdateAbsoluteQuantityData) => loadAdjustmentsControllerUpdateAbsoluteQuantity(data),
    updatePercentageQuantityAdjustment: (data: LoadAdjustmentsControllerUpdatePercentageQuantityData) => loadAdjustmentsControllerUpdatePercentageQuantity(data),
    updateCompanyHolidaysAdjustment: (data: LoadAdjustmentsControllerUpdateCompanyHolidaysData) => loadAdjustmentsControllerUpdateCompanyHolidays(data),
    updateReferenceBaseloadCorrection: (data: LoadAdjustmentsControllerUpdateReferenceBaseloadCorrectionData) => loadAdjustmentsControllerUpdateReferenceBaseloadCorrection(data),
    updateRenewableProductionAdjustment: (data: LoadAdjustmentsControllerUpdateRenewableProductionData) => loadAdjustmentsControllerUpdateRenewableProduction(data),

    deleteLoadAdjustment: (data: LoadAdjustmentsControllerDeleteAdjustmentData) => loadAdjustmentsControllerDeleteAdjustment(data),

    requestLoadProfile: (data: LoadPlanningControllerRequestLoadProfileDataData) => loadPlanningControllerRequestLoadProfileData(data),

    fetchLoadForecast: (data: LoadPlanningControllerGetForecastData) => loadPlanningControllerGetForecast(data),
    updateLoadForecast: (data: LoadPlanningControllerUpdateLoadPlanningData) => loadPlanningControllerUpdateLoadPlanning(data),
    fetchAllLoadPlannings: (data: LoadPlanningControllerGetAllLoadPlanningsData) => loadPlanningControllerGetAllLoadPlannings(data),
    fetchPreviewForecast: (data: LoadPlanningControllerGetPreviewForecastData) => loadPlanningControllerGetPreviewForecast(data),
    fetchEarliestConsumptionPerLocation: (data: LoadPlanningControllerGetEarliestConsumptionPerLocationData) => loadPlanningControllerGetEarliestConsumptionPerLocation(data),

    uploadCustomReferenceBaseLoad: (data: CustomReferenceBaseLoadControllerUploadCustomReferenceBaseLoadData) => customReferenceBaseLoadControllerUploadCustomReferenceBaseLoad(data),
    deleteCustomReferenceBaseLoad: (data: CustomReferenceBaseLoadControllerDeleteCustomReferenceBaseLoadData) => customReferenceBaseLoadControllerDeleteCustomReferenceBaseLoad(data),

    createCustomReferencePrice: (data: StrategySpecificReferencePriceControllerCreateData) => strategySpecificReferencePriceControllerCreate(data),
    updateCustomReferencePrice: (data: StrategySpecificReferencePriceControllerCreateOrUpdateStrategySpecificReferencePriceData) => strategySpecificReferencePriceControllerCreateOrUpdateStrategySpecificReferencePrice(data),

    portfolio: {
      fetchTimeseries: (data: PortfolioOverviewControllerGetPortfolioTimeseriesData) => portfolioOverviewControllerGetPortfolioTimeseries(data),
      fetchOverview: (data: PortfolioOverviewControllerGetPortfolioOverviewData) => portfolioOverviewControllerGetPortfolioOverview(data),
      requestUpdateStrategy: (data: PortfolioOverviewControllerRequestUpdatePortfolioStrategyData) => portfolioOverviewControllerRequestUpdatePortfolioStrategy(data),
      requestStrategy: (data: PortfolioOverviewControllerRequestPortfolioStrategyData) => portfolioOverviewControllerRequestPortfolioStrategy(data),
    },

    documents: {
      static: {
        uploadDocuments: (data: StaticStrategyControllerUploadStrategyDocumentsData) => staticStrategyControllerUploadStrategyDocuments(data),
        getDocumentUrl: (data: StaticStrategyControllerGetStaticStrategyDocumentsData) => staticStrategyControllerGetStaticStrategyDocuments(data),
        deleteDocumentUrl: (data: StaticStrategyControllerDeleteStaticStrategyDocumentsData) => staticStrategyControllerDeleteStaticStrategyDocuments(data),
      },
      hedged: {
        uploadDocuments: (data: StrategyControllerUploadStrategyDocumentsData) => strategyControllerUploadStrategyDocuments(data),
        getDocumentUrl: (data: StrategyControllerGetStrategyDocumentsData) => strategyControllerGetStrategyDocuments(data),
        deleteDocumentUrl: (data: StrategyControllerDeleteStrategyDocumentsData) => strategyControllerDeleteStrategyDocuments(data),
      }
    }
  },

  invoiceAudits: {
    // @ts-ignore
    fetchByCompany: ({ companyId, page, pageSize }: { companyId?: string, page?: number, pageSize?: number }) => invoicesControllerGetInvoices({ companyId, page, pageSize }),
    updateOcrCostDetails: (invoiceId: number, costDetailsDto: OcrExtractedCostDetailsDto) => invoicesControllerUpdateOcrCostDetails({ invoiceId, requestBody: costDetailsDto }),
    updateCostDetailsVerification: (invoiceId: number, costVerificationDto: UpdateCostVerificationOverrideDto) => invoicesControllerUpdateOcrCostVerification({ invoiceId, requestBody: costVerificationDto }),
    // @ts-ignore
    fetchByLocation: ({ locationId, page, pageSize }: { locationId: number, page?: number, pageSize?: number }) => invoicesControllerGetInvoices({ locationId, page, pageSize }),
    // @ts-ignore
    fetchByBuilding: ({ buildingId, page, pageSize }: { buildingId: number, page?: number, pageSize?: number }) => invoicesControllerGetInvoices({ buildingId, page, pageSize }),
    fetchById: (invoiceId: number) => invoicesControllerGetInvoiceById({ invoiceId }),
    // @ts-ignore
    fetchByCompanyV2: ({ companyId, page, pageSize }: { companyId?: string, page?: number, pageSize?: number }) => invoicesControllerGetProcurementInvoices({ companyId, page, pageSize }),
    // @ts-ignore
    fetchByLocationV2: ({ locationId, page, pageSize }: { locationId: number, page?: number, pageSize?: number }) => invoicesControllerGetProcurementInvoices({ locationId, page, pageSize }),
    // @ts-ignore
    fetchByBuildingV2: ({ buildingId, page, pageSize }: { buildingId: number, page?: number, pageSize?: number }) => invoicesControllerGetProcurementInvoices({ buildingId, page, pageSize }),
    fetchByIdV2: (invoiceId: number) => invoicesControllerGetProcurementInvoiceById({ invoiceId }),
    patchInvoiceState: (data: InvoicesControllerUpdateStatusData) => invoicesControllerUpdateStatus(data),
    patchInvoiceNotes: (data: InvoicesControllerUpdateNotesData) => invoicesControllerUpdateNotes(data)
  },

  possibleParentMeters: {
    get: (deviceId: string, buildingId: number) => axios.get(`/meters/${deviceId}/possible-parent-meters?buildingId=${buildingId}`),
  },

  slp: {
    createMeter: (requestBody: CreateSlpMeterRequestDto) => meterControllerCreateSlpMeter({
      requestBody
    }),
    updateMeter: (deviceId: string, requestBody: CreateSlpMeterRequestDto) => meterControllerUpdateSlpMeter({
      deviceId,
      requestBody
    }),
    deleteMeter: (deviceId: string, buildingId: number) => meterControllerDeleteSlpMeter({
      deviceId: deviceId,
      buildingId: buildingId
    }),
    getMeter: (deviceId: string, buildingId: number) => meterControllerGetSlpMeter({
      deviceId: deviceId,
      buildingId: buildingId,
    }),
    getConsumption: (deviceId: string, from: string, to: string) => slpConsumptionControllerGetSlpConsumption({
      deviceId: deviceId,
      from: from,
      to: to,
    }),
    putConsumption: (deviceId: string, consumption: Array<SlpConsumptionDto>) => slpConsumptionControllerUpdateSlpConsumption({
      deviceId: deviceId,
      requestBody: {
        consumption: consumption,
      }
    }),
    getReferences: (deviceId: string) => slpConsumptionControllerGetReferencesForDeviceId({
      deviceId: deviceId,
    }),
    getReadings: (deviceId: string) => slpMeterReadingControllerGetReadings({
      deviceId: deviceId,
    }),
    upsertReadings: (deviceId: string, readings: CreateSlpMeterReadingDto[]) => slpMeterReadingControllerReplaceReadings({
      deviceId: deviceId,
      requestBody: readings,
    })
  },

  consumption: {
    getMany: createCachedApiCall('POST', CONSUMPTION, ({ devices, from, to }) => ({ devices, from, to }), 5),
    getLoadProfile: (params: ConsumptionControllerGetAggregatedConsumptionWithGranularityData) =>
      consumptionControllerGetAggregatedConsumptionWithGranularity(params),
    getSeries: (params: ConsumptionControllerGetConsumptionSeriesData) =>
      consumptionControllerGetConsumptionSeries(params),
  },

  submeterAttribution: {
    fetchByMeterId: (params: SubmeterAttributionControllerGetAttributionData) => submeterAttributionControllerGetAttribution(params),
  },

  consumptionTrends: {
    get: createCachedApiCall<PostConsumptionTrendsResponse>('GET', CONSUMPTION_TRENDS, getConsumptionTrendsPayload, 5),
    post: createCachedApiCall<PostConsumptionTrendsResponse>('POST', CONSUMPTION_TRENDS, postConsumptionTrendsPayload, 5),
  },

  loadProfileDetails: {
    get: createCachedApiCall<GetLoadProfileDetailsResponse>('GET', LOAD_PROFILE_DETAILS, getLoadProfileDetailsPayload, 0),
  },

  peakloadTrends: {
    post: createCachedApiCall<PostPeakLoadTrendsResponse>('POST', PEAKLOAD_TRENDS, postPeakLoadTrendsPayload, 5),
  },

  locationsStats: {
    post: (params: any) => axios.post<TLocationsStatsResponse>(LOCATIONS, params),
    get: (params: ILocationsStatsRequest) => axios.get<TLocationsStatsResponse>(`${LOCATIONS_V2}?${qs.stringify(params)}`),
    getMetrics: (params: CalculationsControllerGetLocationsMetricsData) => calculationsControllerGetLocationsMetrics(params),
    getMetricsCsv: (params: CalculationsControllerGetLocationsMetricsCsvData) => calculationsControllerGetLocationsMetricsCsv(params)
  },

  insightsOnboarding: {
    fetchState: () => insightsOnboardingControllerGetState(),
    start: () => insightsOnboardingControllerStart(),
    complete: () => insightsOnboardingControllerComplete(),
  },

  uiPreferences: {
    fetchOne: (key: UiPreferenceKey) => axios.get<UiPreferenceResponse>(`/ui-preferences/${key}`),
    updateOne: (key: UiPreferenceKey, value: Record<string, unknown>) => axios.put<UiPreferenceResponse>(`/ui-preferences/${key}`, { value }),
  },

  insightSettings: {
    fetchByLocationIds: (locationIds: number[]) => insightSettingsControllerGetForLocations({ locationIds }),
    fetchRecommendationsByLocationIds: (locationIds: number[]) => insightSettingsControllerGetRecommendationsForLocations({ locationIds }),
    applyRecommendations: (meterIds: number[]) => insightSettingsControllerApplyRecommendations({ requestBody: { meterIds } }),
    fetchForMeter: (meterId: number) => insightSettingsControllerGetForMeter({ meterId }),
    fetchRecommendationForMeter: (meterId: number) => insightSettingsControllerGetRecommendationForMeter({ meterId }),
    patchForMeter: (meterId: number, requestBody: PatchInsightInboxSettingsDto) => insightSettingsControllerPatchForMeter({ meterId, requestBody }),
  },

  meterInfrastructureSettings: {
    fetchOne: ({ deviceId, buildingId }: { deviceId: string, buildingId: number }) => axios.get<IFetchOneMeterInfraSettingsResponse>(`/meters/infrastructure-settings/${deviceId}`, { params: { buildingId } }),
    updateOne: ({ deviceId, buildingId }, requestPayload: IUpdateOneMeterInfraSettingsRequest) => axios.put(`/meters/infrastructure-settings/${deviceId}`, requestPayload, { params: { buildingId } }),
    fetchMany: (params: MeterControllerGetBulkInfrastructureSettingsData) => meterControllerGetBulkInfrastructureSettings(params),
    bulkUpdateClassification: (params: MeterControllerPutBulkInfrastructureSettingsData) => meterControllerPutBulkInfrastructureSettings(params),
  },

  baseline: {
    fetch: (requestParams: IFetchBaselineRequest) => axios.get<IGetBaselineResponse[]>('/analytics/baseline', { params: requestParams }),
    fetchOne: ({ id }: { id: string }) => axios.get<IGetBaselineResponse>(`/analytics/baseline/${id}`),
    createOne: (baseline: IPostBaselineRequest) => axios.post<IUpdateOneBaselineResponse>('/analytics/baseline', baseline),
    updateOne: (id: string, baselineUpdate: IPostBaselineRequest) => axios.put<IUpdateOneBaselineResponse>(`/analytics/baseline/${id}`, baselineUpdate),
    deleteOne: ({ id }: { id: string }) => axios.delete<{ id: string; name: string }>(`/analytics/baseline/${id}`),
  },

  visualization: {
    fetch: (requestParams: IFetchVisualizationsRequest) => axios.get<IFetchVisualizationsResponse[]>('/analytics/visualization', { params: requestParams }),
    fetchOne: (requestParams: IFetchOneVisualizationRequest) => axios.get<IFetchOneVisualizationResponse>(`/analytics/visualization/${requestParams.id}`),
    createOne: (visualization: ICreateOneVisualizationRequest) => axios.post<ICreateOneVisualizationResponse>('/analytics/visualization', visualization),
    updateOne: ({ id, updateData }: IUpdateOneVisualizationRequest) => axios.put<IUpdateOneVisualizationResponse>(`/analytics/visualization/${id}`, updateData),
    deleteOne: ({ id }: IDeleteOneVisualizationRequest) => axios.delete<IDeleteOneVisualizationResponse>(`/analytics/visualization/${id}`),
  },

  comparison: {
    fetch: (requestParams: IFetchComparisonsRequest) => axios.get<IFetchComparisonsResponse>('/analytics/comparison', { params: requestParams }),
    fetchOne: (requestParams: IFetchOneComparisonRequest) => axios.get<IFetchOneComparisonResponse>(`/analytics/comparison/${requestParams.id}`),
    createOne: (comparison: ICreateOneComparisonRequest) => axios.post<TCreateOneComparisonResponse>('/analytics/comparison', comparison),
    updateOne: ({ id, updateData }: { id: string, updateData: IUpdateOneComparisonRequest }) => axios.put<TUpdateOneComparisonResponse>(`/analytics/comparison/${id}`, updateData),
    deleteOne: ({ id }: IDeleteOneComparisonRequest) => axios.delete<IDeleteOneComparisonResponse>(`/analytics/comparison/${id}`),
  },

  measureSimulations: {
    fetch: async (
      params: LoadSimulationControllerFindAllForCompanyIdData
    ): Promise<LoadSimulationControllerFindAllForCompanyIdResponse> => {
      const res = await axios.get<LoadSimulationControllerFindAllForCompanyIdResponse>('/load-simulation', { params })
      return res.data
    },
    fetchOne: async (id: number, from: string, to: string): Promise<LoadSimulationControllerFindSimulationByIdResponse> => {
      return await loadSimulationControllerFindSimulationById({ id, from, to })
    },
    deleteOne: async (id: number): Promise<unknown> => {
      return await loadSimulationControllerDeleteSimulationById({ id })
    },
    createOne: async (
      data: LoadSimulationControllerCreateSimulationData
    ): Promise<LoadSimulationControllerCreateSimulationResponse> => {
      const res = await axios.post<LoadSimulationControllerCreateSimulationResponse>('/load-simulation', data.requestBody)
      return res.data
    },
    updateOne: async (
      { id, requestBody }: LoadSimulationControllerUpdateSimulationByIdData
    ): Promise<LoadSimulationControllerUpdateSimulationByIdResponse> => {
      const res = await axios.put<LoadSimulationControllerUpdateSimulationByIdResponse>(`/load-simulation/${id}`, requestBody)
      return res.data
    },
    fetchMainMeters: async (
      params: LoadSimulationControllerGetAvailableMainMetersData
    ): Promise<AvailableMainMeterDto[]> => {
      const res = await axios.get<AvailableMainMeterDto[]>('/load-simulation/available-main-meters', { params })
      return res.data
    },
    fetchGenerationLoadProfiles: async (): Promise<GenerationLoadProfileDto[]> => {
      const res = await axios.get<GenerationLoadProfileDto[]>('/load-simulation/available-generation-load-profiles')
      return res.data
    },
    downloadSimulationCsv: (id: number, from: string, to: string) => loadSimulationControllerGetSimulationCsv({ id, from, to }),
  },

  multilinearRegression: {
    fetch: async (requestParams: MultivariateLinearRegressionControllerGetAllRegressionsData) =>
      await multivariateLinearRegressionControllerGetAllRegressions(requestParams),
    fetchOne: async ({ id }: MultivariateLinearRegressionControllerGetRegressionByIdData) =>
      await multivariateLinearRegressionControllerGetRegressionById({ id }),
    fetchActualVsModelSeries: async ({ id, from, to }: MultivariateLinearRegressionControllerGetActualVsModelSeriesData) =>
      await multivariateLinearRegressionControllerGetActualVsModelSeries({ id, from, to }),
    createOne: async (regression: MultivariateLinearRegressionControllerCreateRegressionData) =>
      await multivariateLinearRegressionControllerCreateRegression(regression),
    updateOne: async ({ id, requestBody }: MultivariateLinearRegressionControllerUpdateRegressionData) =>
      await multivariateLinearRegressionControllerUpdateRegression({ id, requestBody }),
    deleteOne: async ({ id }: MultivariateLinearRegressionControllerDeleteRegressionData) =>
      await multivariateLinearRegressionControllerDeleteRegression({ id }),
  },

  customTimeSeriesData: {
    fetchOne: ({ locationId, timeSeriesId }: { locationId: string; timeSeriesId: string }) => axios.get<TFetchOneCustomTimeSeriesDataResponse>(`/analytics/data-management/data/${locationId}`, { params: { entityId: timeSeriesId } }),
  },

  customTimeSeriesMetadata: {
    fetch: (requestParams: IFetchCustomDataMetaRequest) => axios.get<TFetchCustomDataMetaResponse>('/v2/analytics/data-management/meta', { params: requestParams }),
    fetchOne: ({ id }: { id: string }) => axios.get<IFetchOneCustomTimeSeriesMetaDataResponse>(`/analytics/data-management/meta/${id}`),
    createOne: (customDataMeta: ICreateOneCustomDataMetaRequest) => axios.post<ICreateOneCustomDataMetaResponse>('/analytics/data-management/meta', customDataMeta),
    updateOne: (id: string, customDataMeta: TUpdateOneCustomDataMetaRequest) => axios.put<TUpdateOneCustomDataMetaResponse>(`/analytics/data-management/meta/${id}`, customDataMeta),
    deleteOne: (requestParams: { locationId: number; timeSeriesId: string }) => axios.delete(`/analytics/data-management/meta/${requestParams.locationId}/${requestParams.timeSeriesId}`),
  },

  timeSeriesData: {
    fetch: (requestParams: IFetchTimeSeriesData) => axios.get('/series/data', { params: requestParams, paramsSerializer: { indexes: null } }),
  },

  timeSeries: {
    fetch: (requestParams: { groupId?: string, companyId?: string, regionId?: string | null, locationPgId?: number | null, buildingId?: number, deviceId?: string }) =>
      axios.get('/series', {
        params: {
          groupId: requestParams.groupId,
          companyId: requestParams.companyId,
          regionId: requestParams.regionId,
          locationId: requestParams.locationPgId,
          buildingId: requestParams.buildingId,
          deviceId: requestParams.deviceId
        }
      }),
    fetchAtlasSelectionOptions: (locationId: number) =>
      axios.get<FetchEnpiSeuOptionsResponse>('/iso/enpis/seu-options', { params: { locationId } }).then(response => response.data),
    fetchAtlasSelectionOptionsForLocations: (locationIds: number[]) =>
      axios
        .post<FetchEnpiSeuOptionsResponse>('/iso/enpis/seu-options/batch', { locationIds })
        .then(response => response.data),
  },

  ember: {
    documentAgent: {
      ask: async (args: EmberControllerAskDocumentAssistantData) => await emberControllerAskDocumentAssistant(args),
    },
    chatAgent: {
      ask: async (args: EmberControllerAskChatAssistantData) => await emberControllerAskChatAssistant(args),
    },
    checklistAgent: {
      ask: async (args: EmberControllerAskChecklistAssistantData) => await emberControllerAskChecklistAssistant(args),
    },
    reportAgent: {
      generate: async (args: EmberControllerGenerateReportModuleConfigData) => await emberControllerGenerateReportModuleConfig(
        args)
    },
    cancel: async (args: EmberControllerCancelMessageProcessingData) => await emberControllerCancelMessageProcessing(args),
    propose: async (args: EmberControllerGetProposedQuestionsData) => await emberControllerGetProposedQuestions(args)
  },

  emissions: {
    getEmissionsComparison: (locationId: number, buildingId: number, years: number[]) => emissionsControllerGetEmissionsComparison({
      locationId,
      buildingId,
      years,
    }),
  },

  meterViews: {
    fetch: (companyId: number) => meterViewControllerFindAll({ companyId }),
    fetchOne: (id: number, from: string, to: string) => meterViewControllerFindById({ id, from, to }),
    create: (data: CreateMeterViewDto) => meterViewControllerCreate({ requestBody: data }),
    update: (id: number, data: UpdateMeterViewDto) => meterViewControllerUpdate({ id, requestBody: data }),
    delete: (id: number) => meterViewControllerDelete({ id }),
    createNode: (viewId: number, nodeData: CreateViewNodeDto) => meterViewControllerCreateNode({ id: viewId, requestBody: nodeData }),
    deleteNode: (viewId: number, nodeId: number) => meterViewControllerDeleteNode({ id: viewId, nodeId }),
    updateFolderName: (viewId: number, nodeId: number, name: string) => meterViewControllerUpdateFolderName({ id: viewId, nodeId, requestBody: { name } }),
    fetchMeterInfos: (data: MeterControllerGetMetersInfoByAllowedLocationsData) => meterControllerGetMetersInfoByAllowedLocations(data),
    updateParentNode: (viewId: number, nodeId: number, parentNodeId?: number | null) =>
      meterViewControllerUpdateNodeParent({
        id: viewId,
        nodeId,
        requestBody: {
          parentNodeId
        }
      }),
    fetchSankey: (id: number, from: string, to: string, lang: SupportedLanguage) => sankeyControllerGetMeterViewSankey({ id, from, to, lang }),
  },

  reports: {
    fetch: (companyId: number) => customReportControllerGetAllCustomReports({ companyId }),
    fetchOne: (id: number) => customReportControllerGetCustomReport({ id }),
    create: (data: CreateCustomReportDto) => customReportControllerCreateCustomReport({ requestBody: data }),
    update: (id: number, data: UpdateCustomReportDto) => customReportControllerUpdateCustomReport({ id, requestBody: data }),
    delete: (id: number) => customReportControllerDeleteCustomReport({ id }),
    fetchLogo: (id: number) => customReportControllerGetCustomReportLogo({ id }),
    generateManually: (id: number, email?: string) => customReportControllerGenerateReportManually({ id, requestBody: { email } }),
    generatePdf: (id: number) => axiosPost<Blob>(`/custom-reports/${id}/generate-pdf`, undefined, { responseType: 'blob' }),
    requestReportingUpgrade: () => customReportControllerRequestReportingUpgrade(),
    applyTemplate: (reportId: number, templateId: ApplyTemplateDto['templateId'], timeSeriesId: string) =>
      customReportControllerApplyTemplate({ reportId, requestBody: { templateId, timeSeriesId } }),
    modules: {
      fetch: (reportId: number) => customReportControllerGetReportModules({ reportId }),
      fetchOne: (reportId: number, moduleId: number, limit?: number) => customReportControllerGetModule({ reportId, moduleId, limit }),
      create: (reportId: number, data: CreateReportModuleDto) => customReportControllerCreateReportModule({ reportId, requestBody: data }),
      update: (reportId: number, moduleId: number, data: UpdateReportModuleDto) => customReportControllerUpdateReportModule({ reportId, moduleId, requestBody: data }),
      delete: (reportId: number, moduleId: number) => customReportControllerDeleteReportModule({ reportId, moduleId }),
      reorder: (reportId: number, moduleIds: number[]) => customReportControllerReorderReportModules({ reportId, requestBody: { moduleIds } }),
      fetchForPdf: (token: string, reportId: number) => axiosGet<{
        report: { id: number, name: string, logoId: number | null, logoDataUrl: string | null }
        modules: ResponseReportModuleWithDataDto[]
      }>(`/custom-reports/pdf-access/report/${reportId}/modules?token=${token}`),
      getModuleImage: (reportId: number, moduleId: number) => customReportControllerGetModuleImage({ reportId, moduleId }),
    }
  },

  sentReports: {
    fetch: (reportId: number) => customReportControllerGetSentReports({ reportId }),
    fetchOne: (reportId: number, sentReportId: number) => customReportControllerGetSentReport({ reportId, sentReportId }),
    fetchLogo: (reportId: number, sentReportId: number) => customReportControllerGetSentReportLogo({ reportId, sentReportId }),
    modules: {
      fetch: (reportId: number, sentReportId: number) => customReportControllerGetSentReportModules({ reportId, sentReportId }),
      fetchWithData: (reportId: number, sentReportId: number) => customReportControllerGetSentReportModulesWithData({ reportId, sentReportId }),
      getModuleImage: (reportId: number, sentReportId: number, moduleId: number) => customReportControllerGetSentReportModuleImage({ reportId, sentReportId, moduleId }),
    }
  },

  machineCategories: {
    fetch: () => machineCategoryControllerGetAllCategories(),
  },

  meterSeries: {
    getSeriesForMeter: (meterId: number) => meterSeriesControllerGetSeriesForMeter({ meterId }),
  },

  sso: {
    getSsoStrategies: (companySlug: string) => axiosGet<SsoProvider[]>(`${SSO_STRATEGIES}/${companySlug}`),
    login: (ssoLoginDto: SsoLoginRequestDto) => axiosPost<AuthDto>(SSO_AUTH_LOGIN, { email: ssoLoginDto.email, companySlug: ssoLoginDto.companySlug, accessToken: ssoLoginDto.accessToken }),
    signUp: (data: { companySlug: string; email: string, firstName: string, lastName: string, salutation: string, accessToken: string }, config?: AxiosRequestConfig) => axiosPost<AuthDto>(SSO_AUTH_SIGNUP, data, config),
  },

  shortTermForecast: {
    fetch: (params: ShortTermForecastRequest) =>
      axiosGet<ShortTermForecastResponse>('/short-term-forecasts', { params }),
  },

  gridFeesManagement: {
    getGridUsageFees: (companyId: number, locationId: number) => gridFeesControllerGetGridUsageFees({ companyId, locationId }),
    upsertGridUsageFee: (requestBody: UpsertGridUsagePriceDto) => gridFeesControllerUpsertGridUsageFee({ requestBody }),
    deleteGridUsageFee: (companyId: number, locationId: number, year: number, month: number) => gridFeesControllerDeleteGridUsageFee({ companyId, locationId, year, month }),
    getPeakloadPrices: (companyId: number, locationId: number) => gridFeesControllerGetPeakLoadPrices({ companyId, locationId }),
    upsertPeakloadPrice: (requestBody: UpsertPeakLoadPriceDto) => gridFeesControllerUpsertPeakLoadPrice({ requestBody }),
    deletePeakloadPrice: (companyId: number, locationId: number, year: number, month: number) => gridFeesControllerDeletePeakLoadPrice({ companyId, locationId, year, month }),
  },

  insightInbox: {
    fetchAll: (data?: InsightInboxControllerFindAllData) => insightInboxControllerFindAll(data),
    fetchOne: (data: InsightInboxControllerFindOneData) => insightInboxControllerFindOne(data),
    investigate: (data: InsightInboxControllerInvestigateData) => insightInboxControllerInvestigate(data),
    convert: (data: InsightInboxControllerConvertData) => insightInboxControllerConvert(data),
    resolve: (data: InsightInboxControllerResolveData) => insightInboxControllerResolve(data),
    dismiss: (data: InsightInboxControllerDismissData) => insightInboxControllerDismiss(data),
    assign: (data: InsightInboxControllerAssignData) => insightInboxControllerAssign(data),
    bulkResolve: (data: InsightInboxControllerBulkResolveData) => insightInboxControllerBulkResolve(data),
    bulkDismiss: (data: InsightInboxControllerBulkDismissData) => insightInboxControllerBulkDismiss(data),
    bulkDismissGrouped: (data: InsightInboxControllerBulkDismissGroupedData) => insightInboxControllerBulkDismissGrouped(data),
    fetchDismissalReasonConfig: () => insightInboxControllerGetDismissalReasonConfig(),
    bulkAssign: (data: InsightInboxControllerBulkAssignData) => insightInboxControllerBulkAssign(data),
    setVisibility: (data: InsightInboxControllerSetVisibilityData) => insightInboxControllerSetVisibility(data),
    bulkSetVisibility: (data: InsightInboxControllerBulkSetVisibilityData) => insightInboxControllerBulkSetVisibility(data),
    markViewed: (data: InsightInboxControllerMarkViewedData) => insightInboxControllerMarkViewed(data),
  },

  savingsFlow: {
    createMeasureFromInsight: (insightId: number, dto: CreateIsoMeasureDto) =>
      savingsFlowControllerCreateMeasureFromInsight({ id: insightId, requestBody: dto }),
  },


  auth: {
    sendPasswordResetEmail: (email: string) => authControllerSendPasswordResetEmail({ requestBody: { email } }),
  },

  regions: {
    upsert: (data: RegionControllerUpsertCompanyRegionData) => regionControllerUpsertCompanyRegion(data),
    delete: (data: RegionControllerDeleteCompanyRegionData) => regionControllerDeleteCompanyRegion(data),
  },

  gaps: {
    getByBuilding: (data: GapsControllerGetGapsData) => gapsControllerGetGaps(data),
  },

  dataCompleteness: {
    getDataCompleteness: (data: DataCompletenessControllerGetDataCompletenessData) => dataCompletenessControllerGetDataCompleteness(data),
  },

  upload: {
    uploadManualConsumption: (data: UploadControllerUploadSlpManualConsumptionData) => uploadControllerUploadSlpManualConsumption(data)
  },

  spotMarket: {
    fetchPrices: (data: SpotMarketControllerGetPricesData) => spotMarketControllerGetPrices(data),
    fetchCompanyPrices: (data: SpotMarketControllerGetCompanyPricesData) => spotMarketControllerGetCompanyPrices(data),
  },
}

function axiosGet<T>(url: string, config?: AxiosRequestConfig) {
  return axios
    .get<T>(url, config)
    .then(response => response.data)
    .catch(error => {
      console.error(`API GET Error for URL: ${url}`, error)
      throw error
    })
}

function axiosPost<T>(url: string, data?: any, config?: AxiosRequestConfig) {
  return axios
    .post<T>(url, data, config)
    .then(response => response.data)
    .catch(error => {
      console.error(error)
      throw error
    })
}

function createCachedApiCall<T>(method: 'GET' | 'POST', url: string, payloadGenerator: (args: any) => any, cacheMinutes = CACHE_TTL) {
  return (args: any, config?: AxiosRequestConfig) => {
    const payload = method === 'GET' ? payloadGenerator({ ...args, config }) : payloadGenerator(args)
    const cacheKey = generateCacheKey(url, payload)
    const cachedData = lscache.get(cacheKey)
    if (cachedData) {
      return cachedData
    }

    if (method === 'GET') {
      return axiosGet<T>(url, payload).then(response => {
        lscache.set(cacheKey, response, cacheMinutes)
        return response
      })
    } else if (method === 'POST') {
      return axiosPost<T>(url, payload, config).then(response => {
        lscache.set(cacheKey, response, cacheMinutes)
        return response
      })
    }
  }
}

function generateCacheKey(url: string, args: any) {
  const argString = JSON.stringify(args)
  return `${url}-${argString}`
}
